'use client';

import { motion } from 'framer-motion';
import type { UseCaseCardsSectionData } from '../../types';
import { MediaAsset } from '../MediaAsset';
import { PortableTextContent } from '../PortableTextContent';
import { EASE_OUT_CUBIC, SectionShell } from '../section-shared';

function extractPlainText(value?: unknown[]): string {
  if (!value) return '';
  return value
    .flatMap((block) => {
      const b = block as Record<string, unknown>;
      const children = b?.children as Array<{ text?: string }> | undefined;
      return children?.map((c) => c.text ?? '') ?? [];
    })
    .join('');
}

export function SplitList({ section }: { section: UseCaseCardsSectionData }) {
  return (
    <SectionShell>
      <div className="flex items-start justify-between gap-16 sm:flex-col sm:gap-10">
        <div className="w-[380px] shrink-0 sm:w-full">
          <h2 className="font-semibold text-[34px] text-white leading-[1.2] sm:text-[28px]">{section.title}</h2>
          <PortableTextContent className="mt-5" value={section.description} />
          {section.button?.href && section.button?.label && (
            <a
              className="mt-8 inline-flex h-[50px] items-center rounded-full bg-[#0086FF] px-7 font-medium text-[15px] text-white transition-colors hover:bg-[#0f91ff]"
              href={section.button.href}
              rel={/^https?:\/\//.test(section.button.href) ? 'noreferrer' : undefined}
              target={/^https?:\/\//.test(section.button.href) ? '_blank' : undefined}
            >
              {section.button.label}
            </a>
          )}
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-5 sm:w-full">
          {section.cards?.map((card, index) => (
            <motion.a
              key={card._key || card.title}
              className="flex items-center gap-6 rounded-2xl border border-system-white08 bg-[rgba(255,255,255,0.03)] px-5 py-5 transition-colors hover:bg-[rgba(255,255,255,0.06)]"
              href={card.link?.href || '#'}
              initial={{ opacity: 0, y: 24 }}
              transition={{ duration: 0.5, ease: EASE_OUT_CUBIC, delay: index * 0.08 }}
              viewport={{ once: true, margin: '-40px' }}
              whileInView={{ opacity: 1, y: 0 }}
            >
              <div className="w-[140px] shrink-0 overflow-hidden rounded-xl">
                <MediaAsset className="aspect-[4/3] w-full object-cover" media={card.media} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-3">
                  <span className="font-semibold text-[#0086FF]/40 text-[28px] leading-none">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <h3 className="line-clamp-1 font-semibold text-[17px] text-white leading-[1.3]">{card.title}</h3>
                </div>
                <p className="mt-2 text-sm text-white/50 leading-[1.5]">
                  {extractPlainText(card.description as unknown[])}
                </p>
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </SectionShell>
  );
}
